import {
  ChevronDown, ChevronUp, Image as ImageIcon, Pause, Play, Plus,
  Scissors, Sparkles, Upload, X,
} from 'lucide-react'
import { useResolvedUrl } from '../../utils/useResolvedUrl'
import { ImageDropZone } from '../ImageDropZone'

function ResolvedImg({ url, className, alt = '' }) {
  const resolved = useResolvedUrl(url || '')
  if (!resolved) return null
  return <img src={resolved} alt={alt} className={className} />
}

export function AudioSplitterView(props) {
  const {
    OVERLONG_SEC, WARN_LONG_SEC, adjustTagTime, audioRef, audioUrl,
    autoSplitOverlong, autoSplitTargetSec, currentTime, draggingTagIndex, duration,
    fileInputRef, formatTime, handleClear, handleDrop, handleLoadedMetadata,
    handleSplit, handleTagDragStart, handleTimeUpdate, handleUpload,
    handleWaveformClick, hasOverlong, imageInputRef, imageTargetIndexRef,
    imageTargetTypeRef, imageUploadingIndex, isDragging, isPlaying, markerMode,
    openImagePickerForMarker, openImagePickerForTag, playSelectedSegment,
    processing, removeTag, seekToTag, segmentPreviewPlaying, selectedSegment,
    selectedSegmentIndex, selectedSegmentMarkers, setAutoSplitTargetSec,
    setImageUploadingIndex, setIsDragging, setIsPlaying, setMarkerMode,
    setSegmentMarkers, setSelectedSegmentIndex, setTags, splitSegmentAtMid,
    stopSelectedSegment, tags, togglePlay, tx, updateTag, uploadImage, uploading,
    waveform, waveformRef,
  } = props
  return (
    <div className="space-y-4">
      <audio
        ref={audioRef}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        className="hidden"
      />

      {audioUrl ? (
        <div className="bg-black/30 rounded-xl p-4 border border-white/10">
	          {/* 标题栏 */}
	          <div className="flex items-center justify-between mb-3">
	            <div className="flex items-center gap-2">
	              <span className="text-sm text-white font-medium">{tx({ zh: '音频切分器', en: 'Audio splitter' })}</span>
	              <span className="text-xs text-white/40">{tx({ zh: '点击波形添加切分点', en: 'Click waveform to add split points' })}</span>
	            </div>
	            <button onClick={handleClear} className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white">
	              <X size={16} />
	            </button>
	          </div>

	          {/* 波形显示 */}
	          <div 
	            ref={waveformRef}
	            className="relative h-32 bg-black/50 rounded-lg cursor-crosshair overflow-hidden"
	            onClick={handleWaveformClick}
	          >
	            <input
	              ref={imageInputRef}
	              type="file"
	              accept="image/*"
	              onChange={(e) => {
	                const file = e.target.files?.[0]
	                const idx = imageTargetIndexRef.current
	                if (file !== undefined && idx !== null && idx !== undefined) {
                    if (imageTargetTypeRef.current === 'tag') {
                      setImageUploadingIndex(idx)
                      uploadImage(file).then((url) => {
                        if (url) updateTag(idx, { hasImage: true, imageUrl: url })
                        setImageUploadingIndex(null)
                      })
                    } else {
                      setImageUploadingIndex(idx)
                      uploadImage(file).then((url) => {
                        if (url) {
                          setSegmentMarkers((prev) => {
                            const list = [...(prev[selectedSegmentIndex] || [])]
                            if (!list[idx]) return prev
                            list[idx] = { ...list[idx], imageUrl: url }
                            return { ...prev, [selectedSegmentIndex]: list }
                          })
                        }
                        setImageUploadingIndex(null)
                      })
                    }
                  }
	                e.target.value = ''
	              }}
	              className="hidden"
	            />
	            {/* 波形条 */}
	            <div className="absolute inset-0 flex items-center">
	              {waveform.map((peak, i) => (
	                <div
	                  key={i}
                  className="flex-1 mx-px bg-emerald-500/50 rounded-sm"
                  style={{ height: `${Math.max(peak * 85, 3)}%` }}
                />
              ))}
            </div>
            
	            {/* 切分点标记 */}
	            {tags.map((tag, i) => {
	              const isDraggingThis = draggingTagIndex === i
	              const thumbUrl = tag.imageUrl || ''
	              return (
	              <div
	                key={i}
	                className={`absolute top-0 bottom-5 flex flex-col items-center ${i === 0 ? 'cursor-default' : 'cursor-ew-resize'}`}
	                style={{ left: `${(tag.time / duration) * 100}%`, transform: 'translateX(-50%)' }}
	                onMouseDown={(e) => handleTagDragStart(e, i)}
	              >
                {/* 圆圈编号 */}
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isDraggingThis ? 'bg-orange-400 scale-110' : 'bg-yellow-400'} text-black transition-transform`}>
                  {i + 1}
                </div>
	                {/* 竖线 */}
	                <div className={`w-0.5 flex-1 ${isDraggingThis ? 'bg-orange-400' : 'bg-yellow-400'}`} />
	                {/* 节点内配图：点这里上传/替换 */}
	                <button
	                  type="button"
	                  onClick={(e) => {
	                    e.preventDefault()
	                    e.stopPropagation()
	                    openImagePickerForTag(i)
	                  }}
		                  className={`absolute top-6 w-6 h-6 rounded-md border flex items-center justify-center overflow-hidden ${
		                    tag.hasImage ? 'border-yellow-400/50 bg-yellow-500/10' : 'border-white/15 bg-black/30 hover:bg-black/40'
		                  }`}
		                  title={
		                    tag.hasImage
		                      ? tx({ zh: '点击替换该节点图片', en: 'Click to replace this image' })
		                      : tx({ zh: '点击为该节点添加图片', en: 'Click to add an image' })
		                  }
		                >
		                  {imageUploadingIndex === i ? (
		                    <div className="w-4 h-4 border-2 border-white/30 border-t-yellow-300 rounded-full animate-spin" />
		                  ) : thumbUrl ? (
		                    <ResolvedImg url={thumbUrl} alt="" className="w-full h-full object-cover" />
		                  ) : (
		                    <ImageIcon size={12} className={tag.hasImage ? 'text-yellow-300' : 'text-white/40'} />
		                  )}
		                </button>
	              </div>
	            )})}

                {/* 段内图片标记 PIN（缩略图钉在波形上） */}
                {markerMode &&
	                  selectedSegmentMarkers.map((m, idx) => {
                    const left = `${(m.time / duration) * 100}%`
                    return (
                      <button
                        key={`m-${idx}`}
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          openImagePickerForMarker(idx)
                        }}
	                        className="absolute top-2 -translate-x-1/2"
	                        style={{ left }}
	                        title={tx({ zh: '点击设置/替换该标记图片', en: 'Click to set/replace this marker image' })}
	                      >
	                        <div className="w-9 h-9 rounded-xl border border-white/15 bg-black/40 overflow-hidden shadow-lg">
	                          {m.imageUrl ? (
	                            <ResolvedImg url={m.imageUrl} alt="" className="w-full h-full object-cover" />
	                          ) : (
	                            <div className="w-full h-full flex items-center justify-center">
	                              <ImageIcon size={14} className="text-white/50" />
	                            </div>
	                          )}
                        </div>
                        <div className="w-0.5 h-10 bg-white/30 mx-auto" />
                      </button>
                    )
                  })}
            
            {/* 播放位置 */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white"
              style={{ left: `${(currentTime / duration) * 100}%` }}
            />
            
            {/* 时间刻度 */}
            <div className="absolute bottom-0 left-0 right-0 h-5 bg-black/60 flex items-center justify-between px-2">
              <span className="text-[10px] text-white/50 font-mono">0:00</span>
              <span className="text-[10px] text-white/50 font-mono">{formatTime(duration / 4)}</span>
              <span className="text-[10px] text-white/50 font-mono">{formatTime(duration / 2)}</span>
              <span className="text-[10px] text-white/50 font-mono">{formatTime(duration * 3 / 4)}</span>
              <span className="text-[10px] text-white/50 font-mono">{formatTime(duration)}</span>
            </div>
          </div>

          {/* 播放控制 + 预览（小电视） */}
          <div className="grid md:grid-cols-[1fr,320px] gap-4 mt-3">
            <div className="flex items-center gap-3">
              <button onClick={togglePlay} className="p-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white">
                {isPlaying ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <span className="text-sm text-white/70 font-mono">{formatTime(currentTime)}</span>
              <div className="flex-1" />
	              <span className="text-xs text-white/40">
	                {tx(
	                  { zh: '{n} 个起点 → {n} 段（末尾自动到音频结束）', en: '{n} start points → {n} segments (last goes to audio end)' },
	                  { n: tags.length }
	                )}
	              </span>
	            </div>

            <div className="bg-black/20 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
	                <div className="text-xs text-white/70 font-semibold">{tx({ zh: '预览（选中段落）', en: 'Preview (selected segment)' })}</div>
                {selectedSegment && (
                  <div className="text-[10px] text-white/50 font-mono">
                    {formatTime(selectedSegment.start)} → {formatTime(selectedSegment.end)}
                  </div>
                )}
              </div>
	              <div className="mt-2 rounded-lg bg-black/30 border border-white/10 overflow-hidden aspect-video flex items-center justify-center">
	                {tags[selectedSegmentIndex]?.imageUrl ? (
	                  <ResolvedImg url={tags[selectedSegmentIndex].imageUrl} alt="" className="w-full h-full object-cover" />
	                ) : (
	                  <div className="text-white/40 text-xs">{tx({ zh: '（该节点未配图）', en: '(No image set for this point)' })}</div>
	                )}
	              </div>
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => {
                    if (!selectedSegment) return
                    setSelectedSegmentIndex((prev) => Math.max(0, prev))
                    seekToTag(selectedSegment.start)
                  }}
	                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-xs"
	                  disabled={!selectedSegment}
	                >
	                  {tx({ zh: '跳到起点', en: 'Jump to start' })}
	                </button>
                <button
                  onClick={() => (segmentPreviewPlaying ? stopSelectedSegment() : playSelectedSegment())}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 text-xs font-semibold"
	                  disabled={!selectedSegment}
	                >
	                  {segmentPreviewPlaying ? tx({ zh: '停止该段', en: 'Stop segment' }) : tx({ zh: '播放该段', en: 'Play segment' })}
	                </button>
	              </div>
	              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10">
	                <div className="text-[10px] text-white/50">{tx({ zh: '模式', en: 'Mode' })}</div>
	                <button
	                  onClick={() => setMarkerMode((v) => !v)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    markerMode ? 'bg-purple-500/30 text-purple-200' : 'bg-white/10 hover:bg-white/20 text-white/80'
                  }`}
	                  title={tx({ zh: '切换：切分点模式 / 段内标记模式', en: 'Toggle: split-points / in-segment markers' })}
	                >
	                  {markerMode ? tx({ zh: '段内标记：开', en: 'Markers: ON' }) : tx({ zh: '段内标记：关', en: 'Markers: OFF' })}
	                </button>
	              </div>
            </div>
          </div>

          {/* 切分点列表（= 段落起点列表） */}
          <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
            {tags.map((tag, i) => {
              const endTime = i < tags.length - 1 ? tags[i + 1].time : duration
              const segDuration = endTime - tag.time
              return (
                <div key={i}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSegmentIndex(i)
                      seekToTag(tag.time)
                    }}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left ${
                      selectedSegmentIndex === i ? 'bg-white/10 border-emerald-400/30' : 'bg-black/20 border-white/10 hover:bg-black/30'
                    }`}
                  >
                  <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center text-xs text-black font-bold">
                    {i + 1}
                  </div>
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      min={i === 0 ? 0 : tags[i - 1].time + 0.1}
                      max={i < tags.length - 1 ? tags[i + 1].time - 0.1 : duration}
                      value={tag.time.toFixed(1)}
                      onChange={(e) => adjustTagTime(i, parseFloat(e.target.value) || 0)}
                      disabled={i === 0}
                      className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs font-mono text-center disabled:opacity-50"
                    />
                    <span className="text-[10px] text-white/30">→</span>
                    <span className="text-xs text-white/50 font-mono">{formatTime(endTime)}</span>
	                    <span className={`text-[10px] ${segDuration > OVERLONG_SEC ? 'text-red-400 font-bold' : segDuration > WARN_LONG_SEC ? 'text-yellow-400' : 'text-emerald-400'}`}>
	                      ({segDuration.toFixed(1)}s
	                      {segDuration > OVERLONG_SEC
	                        ? tx({ zh: ' 超长!', en: ' overlong!' })
	                        : segDuration > WARN_LONG_SEC
	                          ? tx({ zh: ' 偏长', en: ' long' })
	                          : ''}
	                      )
	                    </span>
	                  </div>
	                  {segDuration > OVERLONG_SEC && (
	                    <button
	                      onClick={() => splitSegmentAtMid(i)}
	                      className="px-2 py-1 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 text-[10px] font-semibold"
	                      title={tx({ zh: '在中点插入一个切分点', en: 'Insert a split point at the midpoint' })}
	                    >
	                      {tx({ zh: '拆分', en: 'Split' })}
	                    </button>
	                  )}
                  <button
                    onClick={() => updateTag(i, { hasImage: !tag.hasImage })}
	                    className={`p-1.5 rounded-lg border ${
	                      tag.hasImage ? 'bg-yellow-500/20 border-yellow-400/30' : 'bg-white/5 border-white/10 hover:bg-white/10'
	                    }`}
	                    title={
	                      tag.hasImage
	                        ? tx({ zh: '取消配图', en: 'Remove image' })
	                        : tx({ zh: '为该段落起点配图', en: 'Set image for this segment start' })
	                    }
	                  >
	                    <ImageIcon size={14} className={tag.hasImage ? 'text-yellow-300' : 'text-white/40'} />
	                  </button>
                  {i > 0 && (
                    <button
                      onClick={() => removeTag(i)}
                      className="p-1 rounded hover:bg-red-500/30 text-white/40 hover:text-red-400"
                    >
                      <X size={14} />
                    </button>
                  )}
                  </button>
                  {tag.hasImage && (
                    <div className="mt-2 ml-8">
	                      <ImageDropZone
	                        value={tag.imageUrl}
	                        onChange={(url) => updateTag(i, { imageUrl: url })}
	                        label={tx({ zh: '该段配图', en: 'Segment image' })}
	                        compact
	                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* 操作按钮 */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
            <div className="text-xs">
              {tags.length <= 1 ? (
                <span className="text-white/40">{tx({ zh: '不切分：整段音频作为一个段落', en: 'No split: keep the whole audio as one segment' })}</span>
              ) : (
                <span className="text-white/40">
                  {tx({ zh: '建议每段 ', en: 'Suggested per segment: ' })}
                  <span className="text-emerald-400">5-15s</span>，
                  {tx({ zh: '最长 ', en: 'max ' })}
                  <span className="text-yellow-400">30s</span>
                  <span className="text-white/30">{tx({ zh: '（ESP32 内存限制）', en: '(ESP32 memory limit)' })}</span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {hasOverlong && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/20 border border-white/10">
                    <span className="text-[10px] text-white/60">{tx({ zh: '目标', en: 'Target' })}</span>
                    <select
                      value={autoSplitTargetSec}
                      onChange={(e) => setAutoSplitTargetSec(Number(e.target.value))}
                      className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"
                      title={tx({ zh: '自动拆分的目标时长', en: 'Target duration for auto-split' })}
                    >
                      {[8, 12, 15, 20].map((v) => (
                        <option key={v} value={v}>
                          {v}s
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={() => autoSplitOverlong(autoSplitTargetSec)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 text-xs font-semibold"
                    title={tx(
                      { zh: '把所有 >{max}s 的段落自动拆成约 {target}s/段', en: 'Auto-split all segments >{max}s into ~{target}s each' },
                      { max: OVERLONG_SEC, target: autoSplitTargetSec }
                    )}
                  >
                    <Sparkles size={14} />
                    {tx({ zh: '一键拆分超长', en: 'Auto-split overlong' })}
                  </button>
                </div>
              )}
              {tags.length > 1 && (
                <button
                  onClick={() => setTags([{ time: 0, hasImage: true, imageUrl: '' }])}
                  className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
                >
                  {tx({ zh: '清除切分点', en: 'Clear split points' })}
                </button>
              )}
              <button
                onClick={handleSplit}
                disabled={processing || tags.length < 1}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium"
              >
                <Scissors size={16} />
                {processing
                  ? tx({ zh: '处理中...', en: 'Processing…' })
                  : tags.length <= 1
                    ? tx({ zh: '添加为单段落', en: 'Add as single segment' })
                    : tx({ zh: '切分为 {n} 段', en: 'Split into {n} segments' }, { n: tags.length })}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            h-32 border-2 border-dashed rounded-xl cursor-pointer
            flex flex-col items-center justify-center gap-3 transition-all
            ${isDragging ? 'border-emerald-400 bg-emerald-500/10' : 'border-white/20 hover:border-white/40 bg-black/20'}
            ${uploading ? 'pointer-events-none opacity-60' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            className="hidden"
          />
          {uploading ? (
            <div className="flex items-center gap-2 text-white/60">
              <div className="w-5 h-5 border-2 border-white/30 border-t-emerald-400 rounded-full animate-spin" />
              <span>{tx({ zh: '上传中...', en: 'Uploading…' })}</span>
            </div>
          ) : (
            <>
              <Upload size={32} className="text-white/30" />
              <div className="text-center">
                <p className="text-sm text-white/60">{tx({ zh: '拖拽完整音频文件到这里', en: 'Drag your full audio file here' })}</p>
                <p className="text-xs text-white/40 mt-1">
                  {tx({ zh: '上传后可标记切分点，自动生成段落', en: 'After upload, mark split points to generate segments' })}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
